import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, ConfirmModal, RowActions, RowAction, StatusDot, Amount, btnSecondary } from '@redpanda/ui';
import { formatTableDate } from '@redpanda/utils';
import { deleteChangeOrder, decideChangeOrder, ChangeOrderDecision } from '../services/api';
import { useChangeOrdersCache } from '../stores/timeEntryStore';
import { useMembers, memberName } from '../stores/lookupStore';
import { notifyDataChanged, useDataChanged } from '../utils/dataEvents';
import { PMChangeOrder } from '../types/pm';
import { changeOrderTone, toneOf } from '../utils/statusTone';
import ChangeOrderDialog from './ChangeOrderDialog';

type Pending = { order: PMChangeOrder; action: ChangeOrderDecision | 'delete' };

/**
 * 分包分頁下方的「變更單」：開發中追加的需求另外報價
 * 待確認的可以編輯、刪除、標記核准／拒絕；客戶也可以從驗收連結核准（hasShareLink 時提示）
 * 核准後後端建立分包，這裡通知分包、專案重抓（總預算、應收帳款跟著變）
 */
const ChangeOrderSection: React.FC<{ projectId: string; hasShareLink: boolean }> = ({ projectId, hasShareLink }) => {
  const { t, i18n } = useTranslation(['translation', 'pages']);
  const members = useMembers();
  // 有快取：切到分包分頁時直接顯示上次的變更單，不會晚一步才出現
  const { data: orders, refetch: load, key } = useChangeOrdersCache(projectId);
  const [editing, setEditing] = useState<PMChangeOrder | null | undefined>(undefined); // undefined＝關閉、null＝新增
  const [pending, setPending] = useState<Pending | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [key]);
  useDataChanged('packages', load); // 客戶核准後，重新整理專案時一起更新

  const decidedBy = (order: PMChangeOrder) => memberName(members, order.decidedBy) || order.decidedBy;

  const confirm = async () => {
    const { order, action } = pending!;
    setPending(null);
    try {
      if (action === 'delete') {
        await deleteChangeOrder(order.id);
        await load();
        toast.success(t('common.messages.deleteSuccess'));
      } else {
        await decideChangeOrder(order.id, action);
        toast.success(t(action === 'APPROVED' ? 'pmChangeOrders.approvedToast' : 'pmChangeOrders.rejectedToast', { ns: 'pages' }));
        // 核准會建立分包：通知 packages 後，本元件訂閱的 load 也會跟著重抓，不用再自己抓一次
        if (action === 'APPROVED') notifyDataChanged('packages');
        else await load();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('common.messages.operationFailed'));
      load();
    }
  };

  const confirmText = (p: Pending) =>
    p.action === 'APPROVED'
      ? t('pmChangeOrders.confirmApprove', { ns: 'pages', title: p.order.title, amount: p.order.amount.toLocaleString() })
      : p.action === 'REJECTED'
      ? t('pmChangeOrders.confirmReject', { ns: 'pages', title: p.order.title })
      : t('pmChangeOrders.confirmDelete', { ns: 'pages', title: p.order.title });

  return (
    <section className="mt-8">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-fg">{t('pmChangeOrders.title', { ns: 'pages' })}</h2>
          <p className="mt-0.5 text-[13px] text-fg-secondary">
            {t(hasShareLink ? 'pmChangeOrders.hintWithLink' : 'pmChangeOrders.hint', { ns: 'pages' })}
          </p>
        </div>
        <button type="button" onClick={() => setEditing(null)} className={btnSecondary}>
          {t('pmChangeOrders.addButton', { ns: 'pages' })}
        </button>
      </div>

      {orders && orders.length > 0 && (
        <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
          <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <Table style={{ tableLayout: 'fixed', minWidth: '820px' }}>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('pmChangeOrders.fields.title', { ns: 'pages' })}</TableHead>
                  <TableHead numeric className="w-32">{t('pmChangeOrders.fields.amount', { ns: 'pages' })}</TableHead>
                  <TableHead numeric className="w-28">{t('pmChangeOrders.fields.estimatedHours', { ns: 'pages' })}</TableHead>
                  <TableHead className="w-44">{t('pmChangeOrders.fields.status', { ns: 'pages' })}</TableHead>
                  <TableHead className="w-56">{t('common.table.columns.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map(order => (
                  <TableRow key={order.id}>
                    <TableCell className="whitespace-normal">
                      <div className="font-medium text-fg">{order.title}</div>
                      {order.description && <div className="mt-0.5 text-[13px] text-fg-secondary line-clamp-2">{order.description}</div>}
                    </TableCell>
                    <TableCell numeric><Amount value={order.amount} /></TableCell>
                    <TableCell numeric>{order.estimatedHours ?? <span className="text-fg-muted">—</span>}</TableCell>
                    <TableCell className="whitespace-normal">
                      <StatusDot tone={toneOf(changeOrderTone, order.status)} label={t(`pmChangeOrders.status.${order.status}`, { ns: 'pages' })} />
                      {order.decidedAt && (
                        <div className="mt-0.5 text-[13px] text-fg-secondary">
                          {decidedBy(order)} · {formatTableDate(order.decidedAt, i18n.language)}
                          {order.decisionNote && <span className="block truncate" title={order.decisionNote}>「{order.decisionNote}」</span>}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <RowActions>
                        {order.status === 'PENDING' && (
                          <>
                            <RowAction onClick={() => setPending({ order, action: 'APPROVED' })}>{t('pmChangeOrders.approve', { ns: 'pages' })}</RowAction>
                            <RowAction onClick={() => setPending({ order, action: 'REJECTED' })}>{t('pmChangeOrders.reject', { ns: 'pages' })}</RowAction>
                            <RowAction onClick={() => setEditing(order)}>{t('common.actions.edit')}</RowAction>
                          </>
                        )}
                        {order.status !== 'APPROVED' && (
                          <RowAction tone="danger" onClick={() => setPending({ order, action: 'delete' })}>{t('common.actions.delete')}</RowAction>
                        )}
                      </RowActions>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {editing !== undefined && (
        <ChangeOrderDialog
          projectId={projectId}
          order={editing ?? undefined}
          onClose={() => setEditing(undefined)}
          onSaved={() => load()}
        />
      )}
      <ConfirmModal
        isOpen={!!pending}
        onClose={() => setPending(null)}
        onConfirm={confirm}
        title={pending ? t(pending.action === 'delete' ? 'pmChangeOrders.deleteTitle' : pending.action === 'APPROVED' ? 'pmChangeOrders.approve' : 'pmChangeOrders.reject', { ns: 'pages' }) : ''}
        message={pending ? confirmText(pending) : ''}
        confirmText={pending?.action === 'delete' ? t('common.actions.delete') : t('common.actions.confirm')}
        cancelText={t('common.actions.cancel')}
        type={pending?.action === 'delete' ? 'danger' : undefined}
        t={t}
      />
    </section>
  );
};

export default ChangeOrderSection;
